import { useEffect, useId, useRef, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  open: boolean;
  onClose: () => void;
}

const initial = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  vatId: "",
  street: "",
  zip: "",
  city: "",
  country: "Deutschland",
  notes: "",
  acceptTerms: false,
};

const WorkbookOrderModal = ({ open, onClose }: Props) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const update = (k: keyof typeof initial) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const target = e.target as HTMLInputElement;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setForm((f) => ({ ...f, [k]: value as never }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.acceptTerms) {
      toast({ title: "Bitte AGB & Datenschutz bestätigen.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-workbook-order", {
        body: form,
      });
      if (error || (data as any)?.error) {
        throw new Error((data as any)?.error || error?.message || "Fehler");
      }
      onClose();
      setForm(initial);
      navigate("/workbook/danke");
    } catch (err: any) {
      toast({
        title: "Bestellung fehlgeschlagen",
        description: err?.message || "Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wb-order-title"
        className="w-full max-w-2xl bg-card text-foreground rounded-lg shadow-2xl border border-border my-8"
      >
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-1">
              Workbook bestellen
            </div>
            <h2 id="wb-order-title" className="font-display text-xl md:text-2xl font-bold">
              OT-Security Workbook
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Workbook (PDF) + 60 Min. Online Assessment Sparring · 499,00 € netto (593,81 € brutto) · Zahlung per Rechnung (B2B)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="p-2 rounded-md hover:bg-section-alt text-muted-foreground"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Vorname *" name="firstName" value={form.firstName} onChange={update("firstName")} required autoComplete="given-name" />
            <Field label="Nachname *" name="lastName" value={form.lastName} onChange={update("lastName")} required autoComplete="family-name" />
          </div>
          <Field label="Geschäftliche E-Mail *" name="email" type="email" value={form.email} onChange={update("email")} required autoComplete="email" />
          <Field label="Unternehmen *" name="company" value={form.company} onChange={update("company")} required autoComplete="organization" />
          <Field label="USt-IdNr. (für EU-Reverse-Charge optional)" name="vatId" value={form.vatId} onChange={update("vatId")} />
          <Field label="Straße & Nr. *" name="street" value={form.street} onChange={update("street")} required autoComplete="street-address" />
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="PLZ *" name="zip" value={form.zip} onChange={update("zip")} required autoComplete="postal-code" />
            <div className="sm:col-span-2">
              <Field label="Ort *" name="city" value={form.city} onChange={update("city")} required autoComplete="address-level2" />
            </div>
          </div>
          <Field label="Land *" name="country" value={form.country} onChange={update("country")} required autoComplete="country-name" />

          <div>
            <label htmlFor="wb-notes" className="block text-sm font-medium text-foreground mb-1">
              Anmerkungen (optional)
            </label>
            <textarea
              id="wb-notes"
              name="notes"
              value={form.notes}
              onChange={update("notes")}
              rows={3}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
            />
          </div>

          <div className="rounded-md bg-section-alt border border-border p-4 text-xs text-muted-foreground leading-relaxed">
            <p className="mb-2">
              <strong className="text-foreground">Hinweis:</strong> Dies ist ein B2B-Angebot. Sie
              erhalten eine separate Rechnung per E-Mail. Nach Zahlungseingang sende ich Ihnen das
              Workbook als PDF zu.
            </p>
            <label htmlFor="wb-accept-terms" className="flex items-start gap-2 cursor-pointer">
              <input
                id="wb-accept-terms"
                name="acceptTerms"
                type="checkbox"
                checked={form.acceptTerms}
                onChange={update("acceptTerms")}
                className="mt-0.5"
                required
              />
              <span>
                Ich bestelle als Unternehmen und akzeptiere die Verarbeitung meiner Daten zur
                Bestellabwicklung gemäß Datenschutzerklärung.
              </span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 bg-cta text-cta-foreground px-6 py-3 rounded-md font-bold text-sm uppercase tracking-wider shadow-lg shadow-cta/30 hover:bg-cta-hover transition-all disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              Kostenpflichtig bestellen (499,00 € netto)
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Field = ({
  label,
  id,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) => {
  const generatedId = useId();
  const inputId = id ?? `wb-${props.name ?? generatedId}`;
  return (
    <div>
      <label htmlFor={inputId} className="block text-sm font-medium text-foreground mb-1">
        {label}
      </label>
      <input
        id={inputId}
        {...props}
        className="w-full px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-secondary"
      />
    </div>
  );
};

export default WorkbookOrderModal;